import { Injectable, NgZone, OnDestroy, Signal, WritableSignal, computed, inject, signal } from '@angular/core';
import { buildMkSourceConfig } from '@app/shared/utils/streaming-url.utils';
import { DrmDiagnosticsSession, startDrmDiagnostics } from '@app/shared/utils/drm-diagnostics.utils';

declare var mkplayer: any;

// ── 型定義 ──────────────────────────────────────────────

export type PlayerState = 'idle' | 'loading' | 'ready' | 'error' | 'retrying';

export interface PlayerSnapshot {
  state: PlayerState;
  currentTime: number;
  duration: number;
  displayCurrentTime: number;
  displayDuration: number;
  playing: boolean;
  muted: boolean;
  playbackRate: number;
  playbackEnded: boolean;
  playbackStarting: boolean;
  retryCount: number;
  errorDetail: string | null;
  retryable: boolean;
  /**
   * Seeked イベントのたびにインクリメントされるカウンタ。
   * コンポーネントが prev/current を比較してシーク完了を検出するために使用する。
   * destroyPlayer / replacePlayer でリセットされる。
   */
  seekedVersion: number;
}

const IDLE_SNAPSHOT: PlayerSnapshot = {
  state: 'idle',
  currentTime: 0,
  duration: 0,
  displayCurrentTime: 0,
  displayDuration: 0,
  playing: false,
  muted: false,
  playbackRate: 1,
  playbackEnded: false,
  playbackStarting: false,
  retryCount: 0,
  errorDetail: null,
  retryable: false,
  seekedVersion: 0,
};

export interface PlayerMediaSource {
  data?: {
    storage?: {
      streamingURL?: string;
      streamingToken?: string;
    };
  };
}

// ── サービス ────────────────────────────────────────────

@Injectable()
export class PlayerControllerService implements OnDestroy {
  private readonly ngZone = inject(NgZone);

  // ── Signal State ──
  private readonly _snapshot: WritableSignal<PlayerSnapshot> = signal(IDLE_SNAPSHOT);
  readonly snapshot: Signal<PlayerSnapshot> = this._snapshot.asReadonly();
  readonly state: Signal<PlayerState> = computed(() => this._snapshot().state);
  readonly isReady: Signal<boolean> = computed(() => this._snapshot().state === 'ready');
  readonly isPlaying: Signal<boolean> = computed(() => this._snapshot().playing);

  // ── 内部状態 ──
  private sessionId = 0;
  private destroyingVideo = false;
  private destroyed = false;
  /**
   * MKPlayer destroy 中の 1204 エラーを console.error から抑制する。
   * 複数インスタンスが同時に destroy されても安全なよう、参照カウントで管理する。
   */
  private static suppressRefCount = 0;
  private static origConsoleError: ((...args: any[]) => void) | null = null;

  private static beginSuppressMkplayerErrors(): void {
    if (PlayerControllerService.suppressRefCount === 0) {
      PlayerControllerService.origConsoleError = console.error;
      console.error = (...args: unknown[]) => {
        const msg = String(args[0] ?? '');
        if (msg.includes('PlayerError') || msg.includes('1204') || msg.includes('SRC_NOT_SUPPORTED')) return;
        PlayerControllerService.origConsoleError!.apply(console, args);
      };
    }
    PlayerControllerService.suppressRefCount++;
  }

  private static endSuppressMkplayerErrors(): void {
    PlayerControllerService.suppressRefCount--;
    if (PlayerControllerService.suppressRefCount <= 0) {
      PlayerControllerService.suppressRefCount = 0;
      if (PlayerControllerService.origConsoleError) {
        console.error = PlayerControllerService.origConsoleError;
        PlayerControllerService.origConsoleError = null;
      }
    }
  }

  private mkplayerInstance: any = null;
  private diagSession: DrmDiagnosticsSession | null = null;
  private hostElement: HTMLElement | null = null;
  private videoElement: HTMLVideoElement | null = null;
  private currentStreamingUrl: string | null = null;
  private currentStreamingToken: string | null = null;
  // MKPlayer can emit Play before DRM startup fully settles. During that
  // window, a second toggle should not call pause() and turn a recoverable
  // startup delay into a 1303 warning/retry cycle.
  private playRequestPending = false;

  // ユーザー（または明示的な再生再開要求）が「再生したい」状態であることのラッチ。
  // リトライをまたいで維持し、early-ready のたびに自動で play() を呼び直すことで、
  // manifest 伝播待ちの 404 → retry サイクルをユーザーに見せず、スピナーを回し続ける。
  // 1回クリックすれば、再生が実際に始まるまでユーザーの追加操作は不要。
  // pause()/finished/完全破棄/恒久エラー/committed リトライ上限でクリアする。
  private playIntent = false;
  // 現在のロード試行で <video> が canplay（実際に再生可能）に到達したか。
  // play() はこれが true になるまで playFn を呼ばず、canplay で自動再生する。
  // 各ロード試行(initPlayerInternal)の開始でリセットする。
  private mediaCanPlay = false;
  // 外部クロック（VSのWAV-master同期 followVideoToWave）が動画の再生/シークを駆動するモード。
  // true のとき PlayerController 自身は canplay 自動再生を行わない。
  // （自前の canplay 自動再生と followVideoToWave の seek+play が二重駆動すると、
  //   その場で再生→直後にWAV位置へseek、で飛び/巻き戻しが起きるため）
  private externallyDriven = false;
  // committed-start 中に連続で再生に失敗した場合の上限。これを超えたらスピナーを止めて
  // ユーザーに委ねる（無限スピナー回避）。retryCount は実再生開始(handlePlay)でのみ 0 に戻るため、
  // 起動失敗が連続した回数として機能する。RETRY_DELAYS と合わせ ~125s 程度で打ち切る。
  private readonly COMMITTED_STARTUP_MAX_RETRIES = 6;

  // MutationObserver
  private observer: MutationObserver | null = null;

  // Timers
  private readyTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private timePollingId: number | null = null;
  private retryTimerId: ReturnType<typeof setTimeout> | null = null;
  // 視覚スピナー(playbackStarting)のデバウンス。高速レジューム時にチラつかせないため、
  // 論理的な playRequestPending(即時) とは分離し、一定時間起動しなかった場合のみ表示する。
  private playStartDebounceId: ReturnType<typeof setTimeout> | null = null;
  private readonly PLAY_START_DEBOUNCE_MS = 200;
  // committed-start のウォッチドッグ。play() 後に Error も 'playing' も来ないまま
  // スピナーが回り続けるケース（DRM/HDCP の output-restricted で再生が始まらない等）を
  // 一定時間で打ち切り、再生ボタンへ戻す。Error 発生時はクリアされ、リトライ毎に再武装する。
  private startupWatchdogId: ReturnType<typeof setTimeout> | null = null;
  private readonly STARTUP_WATCHDOG_MS = 12_000;

  // Position to seek to once a retried player has loaded enough metadata.
  // Captured in handleError before disposing the failed player so the user
  // does not perceive playback as resetting to the beginning after a retry.
  private retryResumeTime: number | null = null;

  // Video element event cleanup
  private videoEventCleanup: Array<() => void> = [];

  // Retry config (graduated backoff)
  // manifest 404 は streaming locator の伝播遅延が主因で、数秒で 200 に変わることが多い。
  // 序盤を積極的にポーリングして「公開された直後」を素早く捉え、待ち時間を最小化する。
  // （旧 [5s,10s,...] では manifest が ~6s で公開済みでも次の試行が 15s まで来ず取りこぼしていた）
  private readonly RETRY_DELAYS = [1_000, 2_000, 4_000, 8_000, 15_000, 30_000];
  // 15回試して読み込めなければ打ち切り、UI で「時間をおいて再度お試しください」を表示する。
  private readonly MAX_RETRIES = 15;

  // ── ライフサイクル ──

  /**
   * 新規プレイヤーを作成。idle → loading に遷移。
   */
  createPlayer(host: HTMLElement, mediaSource: PlayerMediaSource): void {
    const sid = ++this.sessionId;
    this.destroyPlayerInternal();

    const streamingUrl = mediaSource?.data?.storage?.streamingURL;
    if (!streamingUrl) {
      return;
    }

    this.hostElement = host;
    this.currentStreamingUrl = streamingUrl;
    this.currentStreamingToken = mediaSource?.data?.storage?.streamingToken ?? null;

    this.patchSnapshot({ ...IDLE_SNAPSHOT, state: 'loading' });
    this.flog('createPlayer → loading', { url: this.shortUrl(streamingUrl) });
    this.initPlayerInternal(sid, host, mediaSource);
  }

  /**
   * idle を経由せず直接 loading に遷移（フラッシュ防止）。
   * レコード切替・DRM token更新時に使用。
   */
  replacePlayer(host: HTMLElement, mediaSource: PlayerMediaSource): void {
    const sid = ++this.sessionId;
    this.destroyPlayerInternal();

    const streamingUrl = mediaSource?.data?.storage?.streamingURL;
    if (!streamingUrl) {
      return;
    }

    this.hostElement = host;
    this.currentStreamingUrl = streamingUrl;
    this.currentStreamingToken = mediaSource?.data?.storage?.streamingToken ?? null;

    // idle を飛ばして直接 loading
    this.patchSnapshot({ ...IDLE_SNAPSHOT, state: 'loading' });
    this.flog('replacePlayer → loading', { url: this.shortUrl(streamingUrl) });
    this.initPlayerInternal(sid, host, mediaSource);
  }

  /**
   * プレイヤーを完全破棄。any → idle に遷移。
   */
  destroyPlayer(): void {
    // 既に idle で破棄対象（インスタンス・リトライ）が無ければ no-op。
    // 初期化時に複数経路から destroyPlayer が連続で呼ばれる冗長チャーンを抑える。
    if (this._snapshot().state === 'idle' && !this.mkplayerInstance && this.retryTimerId === null) {
      return;
    }
    const newSid = ++this.sessionId;
    this.flog('destroyPlayer → idle');
    this.destroyPlayerInternal();
    this.patchSnapshot(IDLE_SNAPSHOT);
  }

  ngOnDestroy(): void {
    const newSid = ++this.sessionId;
    this.destroyed = true;
    this.destroyPlayerInternal();
  }

  // ── 再生制御 ──

  play(): void {
    // 「再生したい」意図をラッチする（canplay / リトライ復帰時の自動再開に使う）。
    this.playIntent = true;
    if (this.playRequestPending) return;
    if (!this.mkplayerInstance || !this.mediaCanPlay) {
      // インスタンス未生成、またはまだ canplay 前（DRMライセンス/バッファ未完）。
      // ここで playFn を呼ぶと空転して待たされるため、意図だけラッチしてスピナーを維持し、
      // 実際の playFn 呼び出しは canplay(onCanPlayResume) まで遅延する。
      this.patchSnapshot({ playbackStarting: true });
      // ready 後にコミットされた場合（onVideoElementFound で未武装）の取りこぼしを防ぐ。
      // 既に video 要素があり、まだ武装されていなければウォッチドッグを張る（多重呼び出しでは再武装しない）。
      if (this.videoElement && !this.startupWatchdogId) {
        this.armStartupWatchdog(this.sessionId);
      }
      return;
    }
    const playFn = this.mkplayerInstance.play;
    if (typeof playFn !== 'function') return;
    const snap = this._snapshot();
    if (snap.playbackEnded) {
      try { this.mkplayerInstance.seek?.(0); } catch { /* ignore */ }
      this.patchSnapshot({ playbackEnded: false });
    }
    this.playRequestPending = true;
    this.schedulePlaybackStarting();
    const instance = this.mkplayerInstance;
    try {
      const res = playFn.call(instance);
      res?.catch?.((err: any) => {
        // 古いインスタンス由来の遅延 rejection で現在のスピナーを消さない。
        if (instance !== this.mkplayerInstance) return;
        this.playRequestPending = false;
        if (this.isAutoplayBlocked(err)) {
          // autoplay policy にブロックされた（音声ありメディアで、クリック文脈が切れた
          // リトライ後の自動再生など）。意図を落としてユーザーに再クリックを促す。
          this.playIntent = false;
          this.clearPlaybackStarting();
          return;
        }
        // committed 中はスピナーを維持（Error イベント経由で retry / 自動再開する）。
        if (!this.playIntent) this.clearPlaybackStarting();
      });
    } catch {
      if (instance === this.mkplayerInstance) {
        this.playRequestPending = false;
        if (!this.playIntent) this.clearPlaybackStarting();
      }
    }
  }

  /** play() の Promise rejection がブラウザの autoplay policy 由来かを判定する。 */
  private isAutoplayBlocked(err: any): boolean {
    const name = String(err?.name ?? err?.data?.name ?? '');
    const message = String(err?.message ?? err?.data?.message ?? '').toLowerCase();
    return name === 'NotAllowedError' || message.includes('not allowed') || message.includes('user gesture');
  }

  /** 視覚スピナーをデバウンス後に表示。playing/pause/error が先に来れば表示されない。 */
  private schedulePlaybackStarting(): void {
    const sid = this.sessionId;
    this.clearPlayStartDebounce();
    this.playStartDebounceId = setTimeout(() => {
      this.playStartDebounceId = null;
      // 別セッションへ切替済み、または既に再生開始/停止等で解決済みなら表示しない。
      if (sid !== this.sessionId || !this.playRequestPending) return;
      this.patchSnapshot({ playbackStarting: true });
    }, this.PLAY_START_DEBOUNCE_MS);
  }

  /** デバウンスタイマーを解除し、表示中の視覚スピナーを消す。 */
  private clearPlaybackStarting(): void {
    this.clearPlayStartDebounce();
    this.clearStartupWatchdog();
    if (this._snapshot().playbackStarting) {
      this.patchSnapshot({ playbackStarting: false });
    }
  }

  private clearPlayStartDebounce(): void {
    if (this.playStartDebounceId) {
      clearTimeout(this.playStartDebounceId);
      this.playStartDebounceId = null;
    }
  }

  /**
   * committed-start のウォッチドッグを武装する。STARTUP_WATCHDOG_MS 以内に
   * 'playing'（成功）も Error（→ handleError がクリア）も来なければ、再生は始まらないと判断して
   * スピナーを止め、再生意図を解除して再生ボタンへ戻す。
   */
  private armStartupWatchdog(sid: number): void {
    this.clearStartupWatchdog();
    this.startupWatchdogId = setTimeout(() => {
      this.startupWatchdogId = null;
      if (sid !== this.sessionId || this.destroyed) return;
      this.flog('startup watchdog fired (no playing within budget)');
      // 既に実再生に到達していれば、スピナーを止めるだけ（意図は維持）。
      if (!this._snapshot().playing) {
        this.playIntent = false;
        this.stopRetryTimer();
      }
      this.clearPlaybackStarting();
    }, this.STARTUP_WATCHDOG_MS);
  }

  private clearStartupWatchdog(): void {
    if (this.startupWatchdogId) {
      clearTimeout(this.startupWatchdogId);
      this.startupWatchdogId = null;
    }
  }

  pause(): void {
    // ユーザー（明示的）操作による pause は再生意図を解除し、スピナーも止める。
    // インスタンスが無い（リトライ中）場合も意図クリア＝スピナー停止のキャンセルとして機能させる。
    this.playIntent = false;
    this.playRequestPending = false;
    this.clearPlaybackStarting();
    if (!this.mkplayerInstance) return;
    const res = this.mkplayerInstance.pause?.();
    res?.catch?.(() => { /* ignore */ });
  }

  togglePlay(): void {
    if (this.playRequestPending) return;
    if (this._snapshot().playing) {
      this.pause();
    } else {
      this.play();
    }
  }

  seek(seconds: number): void {
    if (!this.mkplayerInstance?.seek || !Number.isFinite(seconds)) return;
    this.patchSnapshot({ playbackEnded: false });
    this.mkplayerInstance.seek(seconds);
  }

  seekRelative(delta: number): void {
    const current = this._snapshot().currentTime;
    const duration = this._snapshot().duration;
    const target = Math.max(0, Math.min(current + delta, duration));
    this.seek(target);
  }

  setPlaybackRate(rate: number): void {
    if (!Number.isFinite(rate)) return;
    try { this.mkplayerInstance?.setPlaybackRate?.(rate); } catch { /* ignore */ }
    const videoEl = this.videoElement;
    if (videoEl) {
      try { videoEl.playbackRate = rate; } catch { /* ignore */ }
    }
    this.patchSnapshot({ playbackRate: rate });
  }

  cycleRate(): void {
    const rates = [1, 1.25, 1.5, 2, 0.5, 0.75];
    const current = this._snapshot().playbackRate;
    const idx = rates.indexOf(current);
    const next = rates[(idx + 1) % rates.length];
    this.setPlaybackRate(next);
  }

  /**
   * Force mute/unmute. Used by VS (WAV-master) to keep the video silent so the
   * only audible source is the WAV waveform.
   */
  setMuted(muted: boolean): void {
    try {
      if (muted) {
        this.mkplayerInstance?.mute?.();
        this.mkplayerInstance?.setVolume?.(0);
      } else {
        this.mkplayerInstance?.unmute?.();
        this.mkplayerInstance?.setVolume?.(1);
      }
    } catch {
      const videoEl = this.videoElement;
      if (videoEl) videoEl.muted = muted;
    }
    if (this._snapshot().muted !== muted) {
      this.patchSnapshot({ muted });
    }
  }

  toggleMute(): void {
    if (!this.mkplayerInstance) return;
    const muted = !this._snapshot().muted;
    try {
      if (muted) {
        this.mkplayerInstance.mute?.();
        this.mkplayerInstance.setVolume?.(0);
      } else {
        this.mkplayerInstance.unmute?.();
        this.mkplayerInstance.setVolume?.(1);
      }
    } catch {
      const videoEl = this.videoElement;
      if (videoEl) videoEl.muted = muted;
    }
    this.patchSnapshot({ muted });
  }

  /**
   * 外部クロック（VSのWAV-master同期）が動画再生を駆動するモードの切替。
   * VS では followVideoToWave が WAV位置へのseek + play を行うため、true にして
   * PlayerController 自身の canplay 自動再生を無効化し、二重駆動を防ぐ。
   */
  setExternallyDriven(value: boolean): void {
    this.externallyDriven = value;
  }

  // ── アクセサ（波形同期用） ──

  getMkplayerInstance(): any {
    return this.mkplayerInstance;
  }

  /**
   * ビデオセッションが進行中かどうかを返す。
   *
   * `getMkplayerInstance()` は retrying 中に null を返すため、
   * playPause() などの「動画パスを使うべきか」の判定に使ってはならない。
   * このメソッドは state ベースで判定し、retrying 中も true を返す。
   *
   * - true : loading / ready / retrying（動画パス優先）
   * - false: idle / error（動画セッションなし）
   */
  isSessionActive(): boolean {
    const s = this.state();
    return s === 'loading' || s === 'ready' || s === 'retrying';
  }

  getVideoElement(): HTMLVideoElement | null {
    return this.videoElement;
  }

  getHostElement(): HTMLElement | null {
    return this.hostElement;
  }

  getCurrentStreamingToken(): string | null {
    return this.currentStreamingToken;
  }

  getCurrentStreamingUrl(): string | null {
    return this.currentStreamingUrl;
  }

  // ── 内部実装 ──

  private initPlayerInternal(sid: number, host: HTMLElement, mediaSource: PlayerMediaSource): void {
    if (this.destroyed) return;

    const storage = mediaSource?.data?.storage;
    if (!storage?.streamingURL) {
      return;
    }

    // 新しいロード試行。canplay 到達フラグをリセットする。
    this.mediaCanPlay = false;
    this.flog('initPlayer (load attempt)', { url: this.shortUrl(storage.streamingURL) });

    const playerConfig = {
      key: 'd0167b1c-9767-4287-9ddc-e0fa09d31e02',
      playback: { muted: false, autoplay: false },
      ui: false,
      events: {
        [mkplayer.MKPlayerEvent.Error]: (event: any) => {
          if (sid !== this.sessionId) return;
          if (!this.isTransientPlaybackStartWarning(event)) {
            this.diagSession?.onError(event);
          }
          this.handleError(sid, event);
        },
        [mkplayer.MKPlayerEvent.DrmLicenseAdded]: (event: any) => {
          if (sid !== this.sessionId) return;
          this.diagSession?.onLicenseAdded(event);
        },
        [mkplayer.MKPlayerEvent.Play]: () => {
          if (sid !== this.sessionId) return;
          this.handlePlay(sid);
        },
        [mkplayer.MKPlayerEvent.Paused]: () => {
          if (sid !== this.sessionId) return;
          this.handlePause(sid);
        },
        [mkplayer.MKPlayerEvent.Seeked]: () => {
          if (sid !== this.sessionId) return;
          this.handleSeeked(sid);
        },
        [mkplayer.MKPlayerEvent.PlaybackFinished]: () => {
          if (sid !== this.sessionId) return;
          this.handleFinished(sid);
        },
      },
    };

    const instance = new mkplayer.MKPlayer(host, playerConfig);

    // ブラウザ別の manifest URL / DRM 設定を一元生成。
    // 単一のソースキーのみ渡す（両方渡すと MKPlayer が不要なフォーマットを試行して 404 が発生する）。
    const { sourceConfig, profile, url } = buildMkSourceConfig(
      storage.streamingURL,
      storage.streamingToken ?? '',
    );
    this.diagSession = startDrmDiagnostics('player-controller', url, profile);
    this.diagSession.onPlayerCreated(instance);

    this.mkplayerInstance = instance;
    const loadResult = instance.load(sourceConfig);
    Promise.resolve(loadResult)
      .then(() => {
        if (sid !== this.sessionId || this.destroyed || this.mkplayerInstance !== instance) return;
        try { instance.preload?.(); } catch { /* ignore preload support/runtime errors */ }
      })
      .catch(() => {
        // MKPlayer also emits Error events; avoid an unhandled load rejection here.
      });

    // MutationObserver で <video> 要素の挿入を監視
    this.observeVideoElement(sid, host);
  }

  /**
   * MKPlayer が DOM に <video> を挿入するのを MutationObserver で検出する。
   * setTimeout リトライの代わりに確実に検出できる。
   */
  private observeVideoElement(sid: number, host: HTMLElement): void {
    this.observer?.disconnect();

    // 既に video 要素があるかチェック
    const existing = host.querySelector('video') as HTMLVideoElement | null;
    if (existing) {
      this.onVideoElementFound(sid, existing);
      return;
    }

    this.observer = new MutationObserver((mutations) => {
      if (sid !== this.sessionId) {
        this.observer?.disconnect();
        return;
      }
      for (const mutation of mutations) {
        for (const node of Array.from(mutation.addedNodes)) {
          if (node instanceof HTMLVideoElement) {
            this.observer?.disconnect();
            this.onVideoElementFound(sid, node);
            return;
          }
          // MKPlayer は div > video の構造で挿入する場合がある
          if (node instanceof HTMLElement) {
            const video = node.querySelector('video') as HTMLVideoElement | null;
            if (video) {
              this.observer?.disconnect();
              this.onVideoElementFound(sid, video);
              return;
            }
          }
        }
      }
    });

    this.observer.observe(host, { childList: true, subtree: true });

    // 10s フォールバック: MutationObserver でも検出できない場合
    this.readyTimeoutId = setTimeout(() => {
      if (sid !== this.sessionId) return;
      if (this._snapshot().state === 'loading') {
        // video 要素を最終チェック
        const videoEl = host.querySelector('video') as HTMLVideoElement | null;
        if (videoEl) {
          this.onVideoElementFound(sid, videoEl);
        } else {
          this.handleError(sid, { message: 'Video element not found after 10s timeout' });
        }
      }
    }, 10_000);
  }

  /**
   * <video> 要素が見つかった後、canplay で ready に遷移する。
   */
  private onVideoElementFound(sid: number, videoEl: HTMLVideoElement): void {
    if (sid !== this.sessionId) return;
    // MutationObserver の disconnect 後にキュー済みコールバックが実行されるケースや、
    // 既存チェック + Observer が同時に検出するケースで二重呼び出しを防ぐ。
    if (this.videoElement) return;
    this.videoElement = videoEl;
    this.diagSession?.onVideoElement(videoEl);
    this.bindVideoElementEvents(sid, videoEl);
    this.flog('videoElementFound (stay loading until canplay)', { readyState: videoEl.readyState });

    // <video> 要素が見つかっても、manifest が 404 リトライ中だと再生不可。
    // ここでは 'ready'（=プレイヤーUI表示）にせず 'loading' のまま据え置き、
    // canplay（実際に再生可能）で初めて 'ready' に昇格させる。
    // これにより 404 リトライのたびにプレイヤーUIが一瞬表示→消去される点滅を防ぐ。
    // duration は判明していれば先に反映する（durationchange でも後から更新される）。
    this.clearReadyTimeout();
    const duration = this.mkplayerInstance?.getDuration?.() ?? videoEl.duration ?? 0;
    if (Number.isFinite(duration) && duration > 0) {
      this.patchSnapshot({ duration, displayDuration: Math.round(duration) });
    }
    this.startTimePolling(sid);

    // committed-start: 「canplay も playing も来ない（DRM/HDCP の output-restricted や
    // バッファ stall）」ケースを打ち切るウォッチドッグを武装（ロード試行ごとに1回、Error/playing でクリア）。
    if (this.playIntent && !this._snapshot().playing) {
      this.armStartupWatchdog(sid);
    }

    // 要素挿入時点で既に再生可能（キャッシュ等で readyState>=HAVE_FUTURE_DATA）なら即昇格。
    // 通常は readyState=0 で、後続の canplay(onCanPlayResume) が昇格を担う。
    if (videoEl.readyState >= 3) {
      this.promoteToReady(sid);
    }
  }

  /**
   * 実際に再生可能になった（canplay / readyState>=HAVE_FUTURE_DATA）時点で 'ready' に昇格する。
   * 'ready' = プレイヤーUI表示なので、ここまで遅らせることで 404 リトライ中の点滅を防ぐ。
   * retryCount はここではリセットしない（実再生開始 handlePlay でのみ 0 に戻し、committed 上限を機能させる）。
   */
  private promoteToReady(sid: number): void {
    if (sid !== this.sessionId) return;
    this.mediaCanPlay = true;
    if (this._snapshot().state !== 'ready') {
      this.patchSnapshot({ state: 'ready', errorDetail: null, retryable: false });
      this.flog('canplay → ready');
    }
    // 外部駆動（VS WAV-master）では再生は followVideoToWave が seek+play で行うため自動再生しない。
    if (this.externallyDriven) return;
    if (this.playIntent && !this._snapshot().playing) {
      this.reapplyMuted();
      this.play();
    }
  }

  /** スナップショットのミュート状態を現在のインスタンスへ再適用する（retry 後の整合用）。 */
  private reapplyMuted(): void {
    if (!this._snapshot().muted) return;
    try {
      this.mkplayerInstance?.mute?.();
      this.mkplayerInstance?.setVolume?.(0);
    } catch { /* ignore */ }
  }

  private bindVideoElementEvents(sid: number, videoEl: HTMLVideoElement): void {
    const onDurationChange = () => {
      if (sid !== this.sessionId) return;
      if (Number.isFinite(videoEl.duration) && videoEl.duration > 0) {
        const prev = this._snapshot().duration;
        if (Math.abs(videoEl.duration - prev) > 0.01) {
          this.patchSnapshot({
            duration: videoEl.duration,
            displayDuration: Math.round(videoEl.duration),
          });
        }
      }
    };
    const onVolumeChange = () => {
      if (sid !== this.sessionId) return;
      this.patchSnapshot({ muted: videoEl.muted });
    };
    const onNativePlaying = () => {
      if (sid !== this.sessionId) return;
      this.playRequestPending = false;
      // 高速レジューム: デバウンスタイマーを取り消し、スピナーを一度も出さない。
      this.clearPlaybackStarting();
      this.handlePlay(sid);
    };
    const onNativePause = () => {
      if (sid !== this.sessionId) return;
      this.playRequestPending = false;
      this.handlePause(sid);
    };
    // canplay = 実際に再生可能（DRMライセンス + バッファ確保済み）。committed-start なら
    // ここで初めて自動再生する。video 要素挿入直後に play() すると空転して待たされるため、
    // この時点まで待つことで自動再生の体感待ち時間を実ロード時間まで短縮する。
    // canplay = 実際に再生可能（DRMライセンス + バッファ確保済み）。ここで初めて 'ready' に昇格し、
    // プレイヤーUIを表示する。404 リトライ中は canplay が来ないため 'loading' のまま＝点滅しない。
    const onCanPlayResume = () => {
      if (sid !== this.sessionId) return;
      this.promoteToReady(sid);
    };
    const onResumeAfterRetry = () => {
      if (sid !== this.sessionId) return;
      const seekTo = this.retryResumeTime;
      if (seekTo === null) return;
      this.retryResumeTime = null;
      try { this.mkplayerInstance?.seek?.(seekTo); } catch { /* ignore */ }
    };
    // MKPlayer はシーク完了・再生開始時に videoEl.playbackRate を 1 にリセットすることがある。
    // ratechange を監視してスナップショットの倍速を再適用する。
    const onRateChange = () => {
      if (sid !== this.sessionId) return;
      const desired = this._snapshot().playbackRate;
      if (Math.abs(videoEl.playbackRate - desired) > 0.01) {
        try { videoEl.playbackRate = desired; } catch { /* ignore */ }
      }
    };

    videoEl.addEventListener('loadedmetadata', onDurationChange);
    videoEl.addEventListener('loadedmetadata', onResumeAfterRetry, { once: true });
    videoEl.addEventListener('durationchange', onDurationChange);
    videoEl.addEventListener('volumechange', onVolumeChange);
    videoEl.addEventListener('playing', onNativePlaying);
    videoEl.addEventListener('pause', onNativePause);
    videoEl.addEventListener('ratechange', onRateChange);
    videoEl.addEventListener('canplay', onCanPlayResume);

    this.videoEventCleanup.push(
      () => videoEl.removeEventListener('loadedmetadata', onDurationChange),
      () => videoEl.removeEventListener('loadedmetadata', onResumeAfterRetry),
      () => videoEl.removeEventListener('durationchange', onDurationChange),
      () => videoEl.removeEventListener('volumechange', onVolumeChange),
      () => videoEl.removeEventListener('playing', onNativePlaying),
      () => videoEl.removeEventListener('pause', onNativePause),
      () => videoEl.removeEventListener('ratechange', onRateChange),
      () => videoEl.removeEventListener('canplay', onCanPlayResume),
    );
  }

  // ── MKPlayer イベントハンドラ ──

  private handlePlay(sid: number): void {
    if (sid !== this.sessionId || this.destroyingVideo) return;
    // 実再生が始まったので起動失敗カウンタ(retryCount)とエラー表示をリセットする。
    // これにより committed リトライ上限は「再生開始までの連続失敗回数」として機能し、
    // 再生開始後に発生したエラー(網切れ等)からの自動復帰は再び満額の予算で行える。
    this.patchSnapshot({ playing: true, playbackEnded: false, retryCount: 0, errorDetail: null, retryable: false });
    this.startTimePolling(sid);
    // MKPlayer が play 開始時に videoEl.playbackRate をリセットしている場合に再適用する。
    const rate = this._snapshot().playbackRate;
    if (rate !== 1) {
      const videoEl = this.videoElement;
      if (videoEl && Math.abs(videoEl.playbackRate - rate) > 0.01) {
        try { videoEl.playbackRate = rate; } catch { /* ignore */ }
      }
      try { this.mkplayerInstance?.setPlaybackRate?.(rate); } catch { /* ignore */ }
    }
  }

  private handlePause(sid: number): void {
    if (sid !== this.sessionId || this.destroyingVideo) return;
    // committed-start 中（再生確定前）に発火する内部 pause（バッファリング等）では、
    // スピナーと再生意図を維持する。ユーザー操作の pause は public pause() が先に
    // playIntent を false にしているため、ここを通過して通常どおり停止する。
    if (this.playIntent && !this._snapshot().playing) return;
    this.playRequestPending = false;
    this.clearPlayStartDebounce();
    this.patchSnapshot({ playing: false, playbackStarting: false });
    this.stopTimePolling();
  }

  private handleSeeked(sid: number): void {
    if (sid !== this.sessionId || this.destroyingVideo) return;
    this.patchSnapshot({ seekedVersion: this._snapshot().seekedVersion + 1 });
    this.updateTimeOnce();
  }

  private handleFinished(sid: number): void {
    if (sid !== this.sessionId || this.destroyingVideo) return;
    this.playIntent = false;
    this.playRequestPending = false;
    this.clearPlayStartDebounce();
    const { duration, displayDuration } = this._snapshot();
    this.patchSnapshot({
      playing: false,
      playbackEnded: true,
      playbackStarting: false,
      // MediaKind は PlaybackFinished を currentTime < duration で発火させることがある。
      // duration が確定していれば末尾にスナップし、波形カーソルが waveEndSec に届くようにする。
      ...(duration > 0 ? { currentTime: duration, displayCurrentTime: displayDuration } : {}),
    });
    this.stopTimePolling();
  }

  private handleError(sid: number, error: any): void {
    if (sid !== this.sessionId) return;
    if (this.destroyingVideo || this.destroyed) return;

    // 起動デバウンス中ならタイマーを取り消す（エラー後にスピナーが遅れて出るのを防ぐ）。
    this.clearPlayStartDebounce();
    // Error が来た時点でウォッチドッグは役目を終える。retry の backoff 待ちで誤発火しないよう
    // 解除し、次の play()（自動再開）で再武装する。
    this.clearStartupWatchdog();

    if (this.isTransientPlaybackStartWarning(error)) {
      this.playRequestPending = false;
      this.patchSnapshot({
        playing: false,
        // committed 中は MKPlayer が自力で再生開始に再挑戦するためスピナーを維持する。
        playbackStarting: this.playIntent,
        errorDetail: null,
        retryable: false,
      });
      this.stopTimePolling();
      return;
    }

    const retryable = this.isRetryableError(error);
    const detail = this.extractErrorDetail(error);
    const code = error?.code ?? error?.data?.code;

    this.playRequestPending = false;

    if (!retryable) {
      this.playIntent = false;
      this.flog('error (non-retryable) → error', { code, detail });
      this.patchSnapshot({
        state: 'error',
        playing: false,
        playbackStarting: false,
        errorDetail: detail,
        retryable: false,
      });
      this.stopTimePolling();
      this.disposePlayerForRetry(sid);
      return;
    }

    const currentRetry = this._snapshot().retryCount;
    if (currentRetry >= this.MAX_RETRIES) {
      this.playIntent = false;
      this.flog('error (MAX_RETRIES reached) → error', { code, retryCount: currentRetry });
      this.patchSnapshot({
        state: 'error',
        playing: false,
        playbackStarting: false,
        errorDetail: detail,
        retryable: false,
      });
      this.stopTimePolling();
      this.disposePlayerForRetry(sid);
      return;
    }

    // committed-start 中（ユーザーが再生確定済み）に起動失敗が一定回数に達したら、
    // 長時間スピナーを回し続けず打ち切ってユーザーに委ねる（裏の自動リトライは MAX_RETRIES まで継続）。
    // retryCount は実再生開始(handlePlay)でのみ 0 に戻るため、連続起動失敗回数として機能する。
    if (this.playIntent && currentRetry >= this.COMMITTED_STARTUP_MAX_RETRIES) {
      this.playIntent = false;
      this.clearPlaybackStarting();
    }

    const captured = this.mkplayerInstance?.getCurrentTime?.()
      ?? this.videoElement?.currentTime;
    if (Number.isFinite(captured) && captured > 0) {
      this.retryResumeTime = captured;
    }

    this.patchSnapshot({
      state: 'retrying',
      playing: false,
      // committed-start 中は「リトライ中」表示に切り替えず、スピナーを回し続ける
      // （表示側で playbackStarting を見て統一スピナーを出す）。
      playbackStarting: this.playIntent,
      retryCount: currentRetry + 1,
      errorDetail: detail,
      retryable: true,
    });
    this.stopTimePolling();
    this.disposePlayerForRetry(sid);
    this.flog('error (retryable) → retrying', { code, retryCount: currentRetry + 1 });
    this.scheduleRetry(sid, currentRetry);
  }

  // ── リトライ ──

  private scheduleRetry(sid: number, retryIndex: number): void {
    this.stopRetryTimer();
    const delay = this.RETRY_DELAYS[Math.min(retryIndex, this.RETRY_DELAYS.length - 1)];
    this.flog('scheduleRetry', { retryIndex, delayMs: delay });

    this.retryTimerId = setTimeout(() => {
      if (sid !== this.sessionId || this.destroyed) return;
      if (!this.hostElement || !this.currentStreamingUrl) return;

      // retrying → loading
      this.patchSnapshot({ state: 'loading' });
      this.initPlayerInternal(sid, this.hostElement!, {
        data: {
          storage: {
            streamingURL: this.currentStreamingUrl!,
            streamingToken: this.currentStreamingToken ?? undefined,
          },
        },
      });
    }, delay);
  }

  // ── エラー分類 ──

  private isRetryableError(error: any): boolean {
    if (this.destroyingVideo) return false;

    const code = error?.code ?? error?.data?.code;
    const message = String(error?.message ?? '');

    // DRM 系は non-retryable
    if (message.includes('LICENSE') || message.includes('DRM')) return false;
    if (code === 401 || code === 403) return false;

    // 404, 503, network error は retryable
    if (code === 404 || code === 503) return true;
    if (message.includes('NETWORK') || message.includes('timeout')) return true;

    // src="" エラー (1204) は destroy 時以外 retryable
    if (code === 1204) return true;

    // デフォルト: retryable（CDN遅延の可能性）
    return true;
  }

  private isTransientPlaybackStartWarning(error: any): boolean {
    const code = error?.code ?? error?.data?.code;
    const name = String(error?.name ?? error?.data?.name ?? '');
    const message = String(error?.message ?? error?.data?.message ?? '');
    return (
      code === 1303 ||
      name.includes('PLAYBACK_COULD_NOT_BE_STARTED') ||
      message.includes('PLAYBACK_COULD_NOT_BE_STARTED')
    );
  }

  private extractErrorDetail(error: any): string {
    if (!error) return 'Unknown error';
    const code = error?.code ?? error?.data?.code ?? '';
    const msg = error?.message ?? '';
    return code ? `[${code}] ${msg}` : String(msg) || 'Unknown error';
  }

  // ── Time Polling (playing中のみ) ──

  private startTimePolling(sid: number): void {
    this.stopTimePolling();
    const tick = (): void => {
      if (sid !== this.sessionId || this.destroyed) {
        this.timePollingId = null;
        return;
      }
      this.updateTimeOnce();
      this.timePollingId = requestAnimationFrame(tick);
    };
    this.timePollingId = requestAnimationFrame(tick);
  }

  private stopTimePolling(): void {
    if (this.timePollingId !== null) {
      cancelAnimationFrame(this.timePollingId);
      this.timePollingId = null;
    }
  }

  private updateTimeOnce(): void {
    const player = this.mkplayerInstance;
    const videoEl = this.videoElement;
    if (!player && !videoEl) return;

    const current = player?.getCurrentTime
      ? Number(player.getCurrentTime())
      : Number(videoEl?.currentTime ?? 0);
    const snap = this._snapshot();
    const videoDuration = Number(videoEl?.duration ?? Number.NaN);
    const durationRaw =
      Number.isFinite(videoDuration) && videoDuration > 0
        ? videoDuration
        : snap.duration <= 0 && player?.getDuration
          ? Number(player.getDuration())
          : Number.NaN;

    const patch: Partial<PlayerSnapshot> = {};

    if (Number.isFinite(current)) {
      const roundedCurrent = Math.round(current);
      const displayCurrent = snap.displayDuration
        ? Math.min(roundedCurrent, snap.displayDuration)
        : roundedCurrent;
      patch.currentTime = current;
      patch.displayCurrentTime = displayCurrent;
    }

    if (Number.isFinite(durationRaw) && durationRaw > 0) {
      const prevDuration = this._snapshot().duration;
      if (Math.abs(durationRaw - prevDuration) > 0.01) {
        patch.duration = durationRaw;
        patch.displayDuration = Math.round(durationRaw);
      }
    }

    // muted 追従
    if (player?.isMuted) {
      try { patch.muted = !!player.isMuted(); } catch { patch.muted = !!videoEl?.muted; }
    } else if (videoEl) {
      patch.muted = videoEl.muted;
    }

    if (Object.keys(patch).length > 0) {
      this.patchSnapshot(patch);
    }
  }

  // ── 内部ユーティリティ ──

  private patchSnapshot(partial: Partial<PlayerSnapshot>): void {
    this.ngZone.run(() => {
      this._snapshot.update(prev => ({ ...prev, ...partial }));
    });
  }

  /**
   * 状態遷移／ライフサイクルの1行ログ。manifest 404 リトライや再生成ループの切り分け用。
   * MKPlayer の冗長ログと混ざらないよう専用プレフィックスで、低頻度イベントのみ出す。
   */
  private flog(event: string, extra?: Record<string, unknown>): void {
    const s = this._snapshot();
    console.info(`[player-controller][flow] ${event}`, {
      sid: this.sessionId,
      state: s.state,
      retryCount: s.retryCount,
      playIntent: this.playIntent,
      mediaCanPlay: this.mediaCanPlay,
      externallyDriven: this.externallyDriven,
      ...extra,
    });
  }

  /** ログ用に URL を短縮（.ism より後ろのパス末尾だけ）。 */
  private shortUrl(url: string | null | undefined): string {
    if (!url) return '(none)';
    const m = url.match(/\/([^/]+\.ism[^?#]*)/);
    return m ? m[1] : url.slice(-48);
  }

  private destroyPlayerInternal(): void {
    this.destroyingVideo = true;
    this.clearAllTimers();
    this.clearVideoEvents();
    this.observer?.disconnect();
    this.observer = null;
    this.videoElement = null;
    this.playRequestPending = false;
    this.mediaCanPlay = false;
    // 完全破棄（レコード切替・ホスト破棄）では再生意図ラッチも解除する。
    // ※ disposePlayerForRetry（リトライ用の軽量破棄）では playIntent を維持する。
    this.playIntent = false;
    // Discard any pending retry resume position so a user-initiated load
    // (record switch, full destroy) cannot seek into a stale time captured
    // from a previous recording.
    this.retryResumeTime = null;

    const instance = this.mkplayerInstance;
    this.mkplayerInstance = null;

    if (instance) {
      // MKPlayer は destroy 時に video.src="" → MEDIA_ERR_SRC_NOT_SUPPORTED (1204) を
      // 内部で非同期に console.error する。参照カウントで安全に抑制する。
      PlayerControllerService.beginSuppressMkplayerErrors();
      try { instance.destroy?.(); } catch { /* suppress */ }
      // MKPlayer の非同期 destroy 完了を待ってからフラグをリセットする。
      // destroy 中に handleError が呼ばれても destroyingVideo = true で無視される。
      setTimeout(() => {
        this.destroyingVideo = false;
        PlayerControllerService.endSuppressMkplayerErrors();
      }, 500);
    } else {
      this.destroyingVideo = false;
    }
  }

  /**
   * リトライ用の軽量破棄。hostElement は保持する。
   */
  private disposePlayerForRetry(sid: number): void {
    this.stopRetryTimer();
    this.clearReadyTimeout();
    this.stopTimePolling();
    this.clearVideoEvents();
    this.observer?.disconnect();
    this.observer = null;
    this.videoElement = null;
    this.playRequestPending = false;

    this.destroyingVideo = true;
    const instance = this.mkplayerInstance;
    this.mkplayerInstance = null;

    // MKPlayer の destroy を microtask で遅延実行する。
    // sid チェックで、既に新セッションが開始されていた場合は destroyingVideo を触らない。
    const capturedSid = sid;
    queueMicrotask(() => {
      PlayerControllerService.beginSuppressMkplayerErrors();
      try { instance?.destroy?.(); } catch { /* ignore */ }
      setTimeout(() => {
        // 新セッションが開始されていなければフラグをリセット
        if (this.sessionId === capturedSid) {
          this.destroyingVideo = false;
        }
        PlayerControllerService.endSuppressMkplayerErrors();
      }, 500);
    });
  }

  private clearAllTimers(): void {
    this.clearReadyTimeout();
    this.stopTimePolling();
    this.stopRetryTimer();
    this.clearPlayStartDebounce();
    this.clearStartupWatchdog();
  }

  private clearReadyTimeout(): void {
    if (this.readyTimeoutId) {
      clearTimeout(this.readyTimeoutId);
      this.readyTimeoutId = null;
    }
  }

  private stopRetryTimer(): void {
    if (this.retryTimerId) {
      clearTimeout(this.retryTimerId);
      this.retryTimerId = null;
    }
  }

  private clearVideoEvents(): void {
    this.videoEventCleanup.forEach(cleanup => cleanup());
    this.videoEventCleanup = [];
  }
}
